<template>
  <div class="main-layout">
    <header class="navbar">
      <div class="nav-inner">
        <router-link to="/" class="logo">
          <img class="logo-mark" src="/favicon.svg" alt="星轨影像工坊">
          <span class="logo-text">星轨影像工坊</span>
          <span class="logo-badge">AI Video Studio</span>
        </router-link>
        <nav class="nav-links">
          <router-link to="/" class="nav-link" active-class="--active">首页</router-link>
          <router-link to="/pricing" class="nav-link" active-class="--active">方案</router-link>
          <router-link to="/knowledge" class="nav-link" active-class="--active">创作知识库</router-link>
          <router-link to="/channel" class="nav-link" active-class="--active">矩阵合作</router-link>
        </nav>
        <div class="nav-right">
          <template v-if="isLoggedIn">
            <router-link to="/user/new-video" class="btn btn-primary btn-sm">进入创作台</router-link>
            <div class="user-avatar-sm">{{ username.charAt(0).toUpperCase() }}</div>
          </template>
          <template v-else>
            <router-link to="/login" class="btn btn-ghost btn-sm">登录</router-link>
            <router-link to="/register" class="btn btn-primary btn-sm">免费注册</router-link>
          </template>
        </div>
      </div>
      <div class="nav-glow"></div>
    </header>

    <main class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <footer class="footer">
      <div class="footer-inner">
        <div class="footer-grid">
          <div>
            <div class="footer-brand"><img class="logo-mark small" src="/favicon.svg" alt="星轨影像工坊">星轨影像工坊</div>
            <p>面向 AI 漫剧、口播短剧、矩阵账号的全流程视频生产平台。</p>
          </div>
          <div>
            <h4>创作流程</h4>
            <router-link to="/user/new-video">分镜创作</router-link>
            <router-link to="/user/videos">成片剪辑</router-link>
            <router-link to="/user/prompt-library">提示词库</router-link>
          </div>
          <div>
            <h4>发布增长</h4>
            <router-link to="/user/publish-center">一键发布</router-link>
            <router-link to="/user/schedule-center">排期任务</router-link>
            <router-link to="/channel">矩阵合作</router-link>
          </div>
          <div>
            <h4>技术栈</h4>
            <p>ComfyUI · WanVideo · LipSync · TTS · 自动字幕</p>
          </div>
        </div>
        <div class="footer-bottom">
          <span>© 2026 星轨影像工坊</span>
          <span>AI 生成内容请按平台规则完成审核后发布</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const isLoggedIn = ref(false)
const username = ref('')

onMounted(() => {
  isLoggedIn.value = !!localStorage.getItem('token')
  username.value = localStorage.getItem('username') || '用户'
})
</script>

<style scoped>
.main-layout { min-height: 100vh; display: flex; flex-direction: column; }
.navbar {
  position: fixed; inset: 0 0 auto 0; z-index: 100;
  background: rgba(7, 12, 24, .78);
  backdrop-filter: blur(22px);
  border-bottom: 1px solid rgba(124,242,255,.14);
}
.nav-inner {
  max-width: 1200px; height: 64px; margin: 0 auto; padding: 0 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.nav-glow { height: 1px; background: linear-gradient(90deg, transparent, rgba(54,228,255,.45), transparent); }
.logo { display: flex; align-items: center; gap: 10px; text-decoration: none; color: var(--text-primary); }
.logo-mark {
  width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center;
}
.logo-mark.small { width: 28px; height: 28px; font-size: 12px; }
.logo-text { font-size: 18px; font-weight: 950; }
.logo-badge {
  font-size: 10px; padding: 3px 8px; border-radius: 999px;
  color: var(--accent-green); background: rgba(88,242,169,.1); border: 1px solid rgba(88,242,169,.18);
}
.nav-links { display: flex; gap: 4px; }
.nav-link {
  padding: 8px 14px; border-radius: 8px; text-decoration: none;
  color: var(--text-secondary); font-size: 13px; font-weight: 800;
}
.nav-link:hover, .nav-link.--active { color: var(--accent-cyan); background: rgba(54,228,255,.07); }
.nav-right { display: flex; align-items: center; gap: 8px; }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 900; text-decoration: none;
  border: 1px solid transparent;
}
.btn-sm { padding: 7px 14px; }
.btn-ghost { color: var(--text-secondary); border-color: var(--border-color); }
.btn-primary { color: #04111d; background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green)); }
.user-avatar-sm {
  width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center;
  background: rgba(141,124,255,.2); color: var(--accent-cyan); font-weight: 950;
}
.main-content { flex: 1; padding-top: 64px; }
.footer { background: #070c18; border-top: 1px solid rgba(124,242,255,.12); padding: 46px 24px 24px; }
.footer-inner { max-width: 1100px; margin: 0 auto; }
.footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 32px; margin-bottom: 28px; }
.footer-brand { display: flex; align-items: center; gap: 10px; font-weight: 950; margin-bottom: 10px; }
.footer h4 { font-size: 13px; margin-bottom: 12px; }
.footer p, .footer a, .footer-bottom { color: var(--text-muted); font-size: 13px; line-height: 1.7; }
.footer a { display: block; text-decoration: none; margin-bottom: 7px; }
.footer a:hover { color: var(--accent-cyan); }
.footer-bottom { border-top: 1px solid rgba(124,242,255,.1); padding-top: 16px; display: flex; justify-content: space-between; gap: 12px; }
@media (max-width: 860px) {
  .nav-links { display: none; }
  .logo-badge { display: none; }
  .nav-inner { height: 58px; padding: 0 12px; gap: 10px; }
  .logo-text { font-size: 15px; }
  .nav-right .btn-sm { padding: 7px 10px; font-size: 12px; }
  .main-content { padding-top: 58px; }
  .footer-grid { grid-template-columns: 1fr; }
  .footer-bottom { flex-direction: column; }
}
</style>
